import add from "date-fns/add";
import differenceInMonths from "date-fns/differenceInMonths";
import startOfMonth from "date-fns/startOfMonth";
import startOfYear from "date-fns/startOfYear";
import sortBy from "lodash/sortBy";
import mapKnownValues from "../../../utils/mapKnownValues";

export type DashboardMetricsAPIResponse = {
  annualMetrics: AnnualData | null;
  monthlyMetrics: {
    monthlyMetricsId: number;
    tenantId: number;
    year: number;
    month: string; // May
    numberOfTransfers: number;
    totalAssetsTransferred: number;
  }[];
};

type MonthlyData = {
  date: Date;
  tenantId: number;
  numberOfTransfers: number;
  totalAssetsTransferred: number;
  isPrediction: boolean;
}[];

class AnnualData {
  annualMetricsId: null | number = null;
  tenantId: null | number = null;
  year: null | number = null;
  participantEarnings: null | number = null;
  employerAppreciationTotal: null | number = null;
  conversionWithManifest: null | number = null;
  conversionWithoutManifest: null | number = null;
  step1Users: null | number = null;
  step2Users: null | number = null;
  step3Users: null | number = null;
  step4Users: null | number = null;

  constructor(params: Partial<AnnualData> = {}) {
    mapKnownValues(this, params);
  }
}

export default class DashboardMetrics {
  annualData: AnnualData = new AnnualData();
  accumulatedMonthlyData: MonthlyData = [];
  monthlyData: MonthlyData = [];

  constructor(params: Partial<DashboardMetrics>) {
    mapKnownValues(this, params);
  }

  // Destructive function that will replace the monthly data with predictive data
  fillWithPredictions(launchDate: Date, year: number) {
    const predict = (type: string) => {
      let startValue = this[type][this[type].length - 1];
      if (!startValue) {
        startValue = {
          date: startOfMonth(launchDate),
          totalAssetsTransferred: 200000,
          tenantId: -1,
          isPrediction: true,
          numberOfTransfers: 10,
        };
      }
      const monthlyData: DashboardMetrics["monthlyData"] = [];
      const yearStart = startOfYear(new Date(`January 1 ${year}`));
      let totalAssetsTransferred = 0;
      let numberOfTransfers = 0;
      for (let month = 0; month < 12; month += 1) {
        const currentDate = add(yearStart, { months: month });
        const monthDifference = differenceInMonths(startValue.date, currentDate) * -1;
        totalAssetsTransferred =
          startValue.totalAssetsTransferred + 0.2 * monthDifference * startValue.totalAssetsTransferred;
        numberOfTransfers = startValue.numberOfTransfers + 0.2 * monthDifference * startValue.numberOfTransfers;
        if (totalAssetsTransferred < 0) {
          totalAssetsTransferred = 0;
          numberOfTransfers = 0;
        }
        const monthlyDataFound = this[type].find((data) => {
          return (
            data.date.getMonth() === currentDate.getMonth() && data.date.getFullYear() === currentDate.getFullYear()
          );
        });
        if (!monthlyDataFound) {
          monthlyData.push({
            tenantId: -1,
            isPrediction: true,
            totalAssetsTransferred: Math.floor(totalAssetsTransferred),
            numberOfTransfers: Math.floor(numberOfTransfers),
            date: currentDate,
          });
        } else {
          monthlyData.push(monthlyDataFound);
        }
      }
      this[type] = sortBy(monthlyData, (data) => data.date);
    };
    predict("monthlyData");
    predict("accumulatedMonthlyData");
  }

  get hasRealData() {
    return this.monthlyData.find((metric) => !metric.isPrediction);
  }

  get totalAssetsTransfersFromMonthlyData() {
    const currentDate = new Date();
    return (
      this.accumulatedMonthlyData.find((data) => {
        return data.date.getFullYear() === currentDate.getFullYear() && data.date.getMonth() === currentDate.getMonth();
      })?.totalAssetsTransferred || 0
    );
  }

  get totalNumberOfTransfersFromMonthlyData() {
    const currentDate = new Date();
    return (
      this.accumulatedMonthlyData.find((data) => {
        return startOfMonth(data.date) === startOfMonth(currentDate);
      })?.numberOfTransfers || 0
    );
  }

  static fromAPI(response: DashboardMetricsAPIResponse) {
    // We must map the response data to make it easier to work with on the front end (map the dates and values)
    const monthlyData = response.monthlyMetrics.map((metric) => {
      return {
        date: new Date(`${metric.month} 1, ${metric.year}`),
        tenantId: metric.tenantId,
        isPrediction: false,
        numberOfTransfers: metric.numberOfTransfers,
        totalAssetsTransferred: metric.totalAssetsTransferred,
      };
    });

    // We have to take the monthly metrics and sum them up from the previous month to make the display correct
    const sortedMonthlyMetrics = sortBy(
      response.monthlyMetrics,
      (metric) => new Date(`${metric.month} 1, ${metric.year}`)
    );
    const accumulatedMonthlyData: DashboardMetrics["monthlyData"] = [];
    sortedMonthlyMetrics.forEach((metric, index) => {
      const prevData = accumulatedMonthlyData[index - 1] || response.monthlyMetrics[index - 1];
      const numberOfTransfers = prevData
        ? prevData.numberOfTransfers + metric.numberOfTransfers
        : metric.numberOfTransfers;
      const totalAssetsTransferred = prevData
        ? prevData.totalAssetsTransferred + metric.totalAssetsTransferred
        : metric.totalAssetsTransferred;
      accumulatedMonthlyData.push({
        date: new Date(`${metric.month} 1, ${metric.year}`),
        tenantId: metric.tenantId,
        isPrediction: false,
        numberOfTransfers,
        totalAssetsTransferred,
      });
    });

    return new DashboardMetrics({
      annualData: new AnnualData(response.annualMetrics || undefined),
      accumulatedMonthlyData,
      monthlyData,
    });
  }
}
